import { ElMessageBox } from 'element-plus';
import * as XLSX from 'xlsx';

/**
 * 将包含 AM/PM 的时间文本转换为 24 小时制。
 */
const convert12HourTo24Hour = (input: string): string => {
    if (typeof input !== 'string') return input as unknown as string;

    const pureTimeMatch = input.match(/^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)\s*$/i);
    if (pureTimeMatch) {
        let hour = parseInt(pureTimeMatch[1], 10);
        const minute = pureTimeMatch[2];
        const second = pureTimeMatch[3] ?? '00';
        const isPM = pureTimeMatch[4].toUpperCase() === 'PM';

        if (isPM && hour !== 12) hour += 12;
        if (!isPM && hour === 12) hour = 0;

        return `${hour.toString().padStart(2, '0')}:${minute}:${second}`;
    }

    return input;
};

/**
 * 规范化单元格文本：
 * - 替换不间断空格(NBSP)为普通空格
 * - 移除零宽空白(BOM/ZW*等)
 * - 去除首尾空白
 */
const sanitizeCellText = (input: string): string => {
    if (typeof input !== 'string') return input as unknown as string;
    const replaced = input
        .replace(/\u00A0/g, ' ')
        .replace(/[\u200B-\u200D\uFEFF]/g, '');
    return replaced.trim();
};

export function getExcelData(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
        // 验证文件类型
        console.log(file);

        console.log(file.name);
        if (!file.name.endsWith('.xlsx')) {
            ElMessageBox.alert(WordList['Please select a file in .xlsx format.'], WordList.TabelDeleteTitle, {
                type: 'error',
                confirmButtonText: WordList.TabelFootSure
            });
            reject(new Error(WordList['Please select a file in .xlsx format.']));
        }

        // 验证文件大小（限制10MB）
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            ElMessageBox.alert(WordList['The file size must not exceed {0}MB.'].format('10'), WordList.TabelDeleteTitle, {
                type: 'error',
                confirmButtonText: WordList.TabelFootSure
            });
            reject(new Error(WordList['The file size must not exceed {0}MB.'].format('10')));
        }

        // 读取文件
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                // 避免返回 Date 对象引入时区/精度误差，使用字符串格式
                const workbook = XLSX.read(data, { type: 'array', cellDates: false, dateNF: 'yyyy/mm/dd' });

                // 获取第一个工作表
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];

                // 转换为JSON格式，第一行作为表头
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                    raw: false
                }) as string[][];

                // 验证行数限制（不包括表头，最多1000行数据）
                const maxRows = 1000;
                if (jsonData.length > maxRows + 1) {
                    ElMessageBox.alert(WordList['You can import up to 1000 entries at a time. Please split your data and try again.'],
                        WordList.TabelDeleteTitle, {
                            type: 'error',
                            confirmButtonText: WordList.TabelFootSure
                        });
                    reject(new Error(WordList['You can import up to 1000 entries at a time. Please split your data and try again.']));
                }

                // 第一行作为表头
                const headers = jsonData[0];
                // 其余行作为数据，转换为对象数组
                const rows = jsonData.slice(1)
                    .map((row) => {
                        const obj: Record<string, any> = {};
                        headers.forEach((header, index) => {
                            const cellValue = row[index];
                            if (typeof cellValue === 'string') {
                                const cleaned = sanitizeCellText(cellValue);
                                obj[header] = convert12HourTo24Hour(cleaned);
                            } else {
                                obj[header] = cellValue;
                            }
                        });
                        return obj;
                    })
                    .filter((row) => Object.values(row).some((value) => value !== null && value !== undefined && String(value).trim() !== ''));

                resolve(rows);
            } catch (error) {
                ElMessageBox.alert(WordList['File parsing failed. Please check the file format.'], WordList.TabelDeleteTitle, {
                    type: 'error',
                    confirmButtonText: WordList.TabelFootSure
                });
                reject(error);
            }
        };

        reader.onerror = () => {
            reject(new Error(WordList['File parsing failed. Please check the file format.']));
        };

        reader.readAsArrayBuffer(file);
    });
}
